import { useAuth0 } from "@auth0/auth0-react";
import { Spin, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setIsAccountDataFilled } from "../../store/common";
import {
  isAccountDataComplete,
  mergeCompanyDataForSettings,
} from "../../utils/accountDataValidation";
import { getCompany, getUser } from "../../utils/users";
import Billing from "./Billing";
import MyProfile from "./MyProfile";
import SettingsNotifications from "./SettingsNotifications";
import { useContextState } from "../../context/ContextProvider";

const Settings = () => {
  const { isLoading, getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [profileLoading, setProfileLoading] = useState(true);

  // Map paths to tab keys
  const pathToTabKey = {
    "/settings": "profile",
    "/settings/profile": "profile",
    "/settings/billing": "billing",
    "/settings/notifications": "notifications",
  };

  // Get active tab key based on current path
  const getActiveTabKey = () => {
    return pathToTabKey[location.pathname] || "profile";
  };

  const [activeKey, setActiveKey] = useState(getActiveTabKey());

  const { setOrgFolderContentsAccountsPage } = useContextState();

  // for reseting the table pagination in org folder contents view
  setOrgFolderContentsAccountsPage(null)

  const syncAccountDataFilled = (profileData, companyData) => {
    const complete = isAccountDataComplete(profileData, companyData);
    dispatch(setIsAccountDataFilled(complete));
    return complete;
  };

  // Fetch profile and company data for validation regardless of active tab
  const fetchAccountData = async () => {
    setProfileLoading(true);
    try {
      const token = await getIdTokenClaims();

      // Fetch user and company data in parallel
      const [userResponse, companyResponse] = await Promise.all([
        getUser(token.__raw),
        getCompany(token.__raw),
      ]);

      const userData = userResponse.data || {};
      const companyRow = companyResponse.data?.company;
      const companyData = mergeCompanyDataForSettings(userData, companyRow);

      syncAccountDataFilled(userData, companyData);
    } catch (error) {
      console.error("Error fetching account data:", error);
      dispatch(setIsAccountDataFilled(false));
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (key) => {
    setActiveKey(key);

    switch (key) {
      case "profile":
        navigate("/settings/profile");
        break;
      case "billing":
        navigate("/settings/billing");
        break;
      case "notifications":
        navigate("/settings/notifications");
        break;
      default:
        navigate("/settings/profile");
    }
  };

  // Update active key when location changes (e.g., from browser navigation)
  useEffect(() => {
    setActiveKey(getActiveTabKey());
  }, [location.pathname]);

  // Redirect to proper tab route on initial load if at base /settings
  useEffect(() => {
    if (location.pathname === "/settings") {
      navigate("/settings/profile");
    }
  }, [location.pathname, navigate]);

  // Fetch account data when component mounts or auth state changes
  useEffect(() => {
    if (!isLoading) {
      fetchAccountData();
    }
  }, [isLoading]);

  if (isLoading || profileLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Tabs activeKey={activeKey} onChange={handleTabChange}>
      <Tabs.TabPane tab="My Profile" key="profile">
        <MyProfile onProfileUpdate={fetchAccountData} />
      </Tabs.TabPane>
      <Tabs.TabPane tab="Billing" key="billing">
        <Billing />
      </Tabs.TabPane>
      <Tabs.TabPane tab="Notifications" key="notifications">
        <SettingsNotifications />
      </Tabs.TabPane>
    </Tabs>
  );
};

export default Settings;
